import { useState } from "react";
import { BewegungsHistorie } from "./wein.jsx";
import { WitterungsPanel } from "./witterungspanel.jsx";
import { bewegungsPreis, gesamtFlaschen, jahrgangTitel, kritikenFuer, regionZuWein, reifeVonJahrgang, trinkfensterText, winzerZuWein } from "../kern/fachlogik.js";
import { fmtEuro, fmtZahl, normiert, wertFormat } from "../kern/format.js";
import { FLASCHENGROESSEN, LISTEN_GRENZEN as G, NV, REIFE_LABEL, SCHEMATA } from "../kern/konstanten.js";
import { Begrenzt, KurzListe } from "../ui/begrenzt.jsx";
import { useEtikett } from "../ui/hooks.jsx";
import { RundumVorschau } from "../ui/flasche.jsx";
import { fotosLoeschen } from "../dienste/fotos.js";
import { Btn, FotoBild, KritikBalken, Leer, LoeschenKnopf, Seitenkopf, TextKnopf, TypPunkt, ZeilenMenue } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";


/** Zeigt das Etikett auf der Jahrgangsseite (nur wenn eines da ist).
    `zu3d`: Es gibt auch ein Rundum-Etikett — Umschalter dorthin. */
function EtikettBild({ wein, jg, etikett, rundumAufnehmen, zu3d = null }) {
  const [gross, setGross] = useState(false);
  const bild = jg.foto || jg.katalogFoto;
  if (!bild) return null;
  return (
    <div className="wl-etikett">
      <button className="wl-etikettbild" onClick={() => setGross(true)} title={t("Vergrößern")} aria-label={t("Etikett vergrößern")}>
        <FotoBild pfad={bild.thumb || bild.pfad} alt={`Etikett ${wein.name} ${jahrgangTitel(wein, jg)}`} />
      </button>
      <div className="wl-etikettaktionen">
        <TextKnopf onClick={etikett.kamera}>{t("Neu aufnehmen")}</TextKnopf>
        <TextKnopf onClick={etikett.datei}>{t("Bild wählen")}</TextKnopf>
        <TextKnopf className="ist-dezent" onClick={etikett.entfernen}>{t("Entfernen")}</TextKnopf>
      </div>
      {zu3d && <TextKnopf onClick={zu3d}>{t("3D-Ansicht zeigen")}</TextKnopf>}
      {!jg.rundum && rundumAufnehmen && (
        <TextKnopf onClick={rundumAufnehmen}>{t("Rundum aufnehmen (3D)")}</TextKnopf>
      )}
      {etikett.laedt && <span className="wl-nebenzeile">{t("Wird hochgeladen …")}</span>}
      {gross && (
        <div className="wl-fotolupe" onClick={() => setGross(false)} role="dialog" aria-label={t("Etikett")}>
          <FotoBild pfad={bild.pfad} alt={`Etikett ${wein.name} ${jahrgangTitel(wein, jg)}`} className="ist-gross" />
        </div>
      )}
    </div>
  );
}


/** Rundum-Etikett als drehbare Flasche (nur wenn eines da ist), mit
    Neu aufnehmen und Entfernen. `zuFoto`: Es gibt auch ein Etikettfoto
    — Umschalter dorthin (gezeigt wird immer nur eines). */
function RundumBild({ wein, jg, setDb, rundumAufnehmen, zuFoto = null }) {
  const r = jg.rundum;
  if (!r) return null;
  const alt = `${t("Rundum-Etikett")} ${wein.name} ${jahrgangTitel(wein, jg)}`;
  const entfernen = () => {
    setDb((prev) => ({
      ...prev,
      weine: prev.weine.map((w) => w.id !== wein.id ? w : {
        ...w, jahrgaenge: w.jahrgaenge.map((j) => {
          if (j.id !== jg.id) return j;
          const { rundum, ...rest } = j;
          return rest;
        }),
      }),
    }));
    fotosLoeschen([r.pfad, r.thumb].filter(Boolean));
  };
  return (
    <div className="wl-etikett">
      <RundumVorschau jg={jg} alt={alt}>
        {String(r.pfad || "").startsWith("wartend:") && <span className="wl-fotowartet">{t("wartet auf Übertragung")}</span>}
      </RundumVorschau>
      <div className="wl-etikettaktionen">
        <TextKnopf onClick={rundumAufnehmen}>{t("Neu aufnehmen")}</TextKnopf>
        <TextKnopf className="ist-dezent" onClick={entfernen}>{t("Entfernen")}</TextKnopf>
      </div>
      {zuFoto && <TextKnopf onClick={zuFoto}>{t("Etikettfoto zeigen")}</TextKnopf>}
    </div>
  );
}


/** Detailseite eines einzelnen Jahrgangs: Analysewerte, Bestand,
    Region, Witterung, Kritiken und Bewegungshistorie. */
function JahrgangDetail({ weinId, jgId, db, setDb, geheZu, oeffneModal, loescheJahrgang, darfWetter = false }) {
  const wein = db.weine.find((w) => w.id === weinId);
  if (!wein) return <Leer text={t("Dieser Wein existiert nicht mehr.")} aktion={<Btn onClick={() => geheZu({ view: "weine" })}>{t("Zur Weinliste")}</Btn>} />;
  const jg = wein.jahrgaenge.find((j) => j.id === jgId);
  if (!jg) return <Leer text={t("Dieser Jahrgang existiert nicht mehr.")} aktion={<Btn onClick={() => geheZu({ view: "wein", id: wein.id })}>{t("Zum Wein")}</Btn>} />;
  /* Der Inhalt hält die Hooks — dieser Rahmen macht die Suche und die
     Leerzustände, damit die Hook-Reihenfolge stabil bleibt. Der key
     setzt zudem den Vergleich zurück, wenn der Jahrgang wechselt. */
  return (
    <JahrgangDetailInhalt key={jg.id} wein={wein} jg={jg} db={db} setDb={setDb}
      geheZu={geheZu} oeffneModal={oeffneModal} loescheJahrgang={loescheJahrgang}
      darfWetter={darfWetter} />
  );
}


function JahrgangDetailInhalt({ wein, jg, db, setDb, geheZu, oeffneModal, loescheJahrgang, darfWetter = false }) {
  const winzer = winzerZuWein(db, wein);
  const region = regionZuWein(db, wein);
  const titel = jahrgangTitel(wein, jg);
  const imBestand = gesamtFlaschen(jg);
  const entnommen = (db.entnahmen || []).filter((e) => e.jgId === jg.id).reduce((s, e) => s + (e.menge || 0), 0);
  const gesamtErworben = imBestand + entnommen;
  const bestandTeile = FLASCHENGROESSEN.filter((g) => ((jg.bestaende || {})[g.key] || 0) > 0);
  const bestandAufteilung = bestandTeile.length && !(bestandTeile.length === 1 && bestandTeile[0].key === "0.75")
    ? bestandTeile.map((g) => `${jg.bestaende[g.key]} × ${g.kurz}`).join(" · ") : null;
  const kritiken = [...kritikenFuer(db, wein.id, null, jg.id)].sort((a, b) => b.wert - a.wert);
  const reife = reifeVonJahrgang(jg);
  const kontext = { weinId: wein.id, jgId: jg.id, jahr: jg.jahr };
  const rundumAufnehmen = () => oeffneModal({ typ: "rundum", kontext });
  const etikett = useEtikett(wein, jg, setDb, rundumAufnehmen);
  /* Etikettfoto und Rundum-Etikett: gezeigt wird immer nur eines, das
     Rundum-Etikett zuerst; umschalten lässt sich, damit das Foto
     erreichbar bleibt (neu aufnehmen, entfernen). */
  const [fotoStatt3d, setFotoStatt3d] = useState(false);
  const hatFoto = !!(jg.foto || jg.katalogFoto);
  const zeige3d = !!jg.rundum && !(fotoStatt3d && hatFoto);

  /* Zugänge ohne eigenen Kaufpreis werden mit dem Jahrgangspreis
     bewertet, damit auch der Anfangsbestand in den Einstand einfließt. */
  const kaeufe = (db.bewegungen || [])
    .filter((b) => b.art === "zugang" && b.jgId === jg.id)
    .map((b) => ({ ...b, preisEff: bewegungsPreis(jg, b) }))
    .filter((b) => b.preisEff != null);
  const kaufMenge = kaeufe.reduce((s, b) => s + (b.menge || 0), 0);
  const einstand = kaufMenge > 0 ? kaeufe.reduce((s, b) => s + (b.menge || 0) * b.preisEff, 0) / kaufMenge : null;
  const quellen = [...new Set((db.bewegungen || []).filter((b) => b.art === "zugang" && b.jgId === jg.id && b.haendler).map((b) => b.haendler))];

  const werte = [
    ["Alkohol", jg.alkohol != null ? `${fmtZahl(jg.alkohol, 1)} % vol` : "—"],
    ["Säure", jg.saeure != null ? `${fmtZahl(jg.saeure, 1)} g/l` : "—"],
    ["Restzucker", jg.restzucker != null ? `${fmtZahl(jg.restzucker, 1)} g/l` : "—"],
    ["pH-Wert", jg.ph != null ? fmtZahl(jg.ph, 2) : "—"],
    ["Zuckerkategorie", jg.zucker || "—"],
    ["Ausbau", jg.ausbau || "—"],
    ["Verschluss", jg.verschluss || "—"],
    ["Schwefel", jg.schwefel || "—"],
    ["Kapsel", jg.kapsel || "—"],
    ["Preis (Flasche)", jg.preis != null ? fmtEuro(jg.preis) : "—"],
    ["Ø Einstand", einstand != null ? `${fmtEuro(einstand)} (${kaufMenge} Fl.)` : "—"],
    ["Bezugsquellen", quellen.length
      ? <KurzListe grenze={G.bezugsquellen.erste} trenner=" · " elemente={quellen.map((q) => <span key={q}>{q}</span>)} />
      : "—"],
    ["Trinkfenster", trinkfensterText(jg)],
    ["Reife", REIFE_LABEL[reife] || "—"],
    ["Lagerort", jg.lagerort || "—"],
  ];

  return (
    <>
      <Seitenkopf
        krume={<><TextKnopf onClick={() => geheZu({ view: "weine" })}>{t("Weine")}</TextKnopf><span> / </span><TextKnopf onClick={() => geheZu({ view: "wein", id: wein.id })}>{wein.name}</TextKnopf><span> / {titel}</span></>}
        titel={`${wein.name} · ${titel}`}
        unterzeile={
          <>
            {winzer && <TextKnopf onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</TextKnopf>}
            {region && <> · <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf>{region.land ? ` (${landName(region.land)})` : ""}</>}
            {wein.appellation && <> · {wein.appellation}</>}
            {wein.klassifikation && <> · {wein.klassifikation}</>}
            {" · "}<TypPunkt typ={wein.typ} />
          </>
        }
        aktionen={
          <>
            <Btn variante="voll" disabled={gesamtFlaschen(jg) === 0} onClick={() => oeffneModal({ typ: "entnahme", kontext })}>{t("Entnehmen")}</Btn>
            <Btn onClick={() => oeffneModal({ typ: "zugang", kontext })}>{t("Zugang")}</Btn>
            <Btn onClick={() => oeffneModal({ typ: "jahrgang", objekt: jg, kontext: { weinId: wein.id, jahrgangId: jg.id } })}>{t("Bearbeiten")}</Btn>
            {!jg.foto && (
              <ZeilenMenue beschriftung="Etikettenfoto hinzufügen"
                knopfLabel={etikett.laedt ? "Wird hochgeladen …" : "Etikett"}
                eintraege={etikett.menue} />
            )}
            <LoeschenKnopf label={t("Jahrgang löschen")} onDelete={() => { loescheJahrgang(wein.id, jg.id); geheZu({ view: "wein", id: wein.id }); }} />
          </>
        }
      />
      {etikett.eingaben}
      {etikett.fehler && <p className="wl-fehler">{etikett.fehler}</p>}

      <section className="wl-abschnitt">
        <div className="wl-jgkopf">
          <div className="wl-jgbestand">
            <div>
              <span className="wl-kennzahl num">{imBestand}</span>
              <span className="wl-kennname">{t("Flaschen im Bestand")}</span>
              {bestandAufteilung && <span className="wl-nebenzeile">{bestandAufteilung}</span>}
            </div>
            <div>
              <span className="wl-kennzahl num">{gesamtErworben}</span>
              <span className="wl-kennname">Insgesamt{entnommen > 0 ? ` · ${entnommen} getrunken` : ""}</span>
            </div>
          </div>
          <div className="wl-etiketten">
            {zeige3d
              ? <RundumBild wein={wein} jg={jg} setDb={setDb} rundumAufnehmen={rundumAufnehmen} zuFoto={hatFoto ? () => setFotoStatt3d(true) : null} />
              : <EtikettBild wein={wein} jg={jg} etikett={etikett} rundumAufnehmen={rundumAufnehmen} zu3d={jg.rundum ? () => setFotoStatt3d(false) : null} />}
          </div>
        </div>
      </section>

      <section className="wl-abschnitt">
        <h2 className="wl-abschnittstitel">{t("Analyse & Kellerdaten")}</h2>
        <dl className="wl-datenliste">
          {werte.map(([k, v]) => (
            <div className="wl-datenpaar" key={k}>
              <dt>{k}</dt><dd>{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="wl-abschnitt">
        <div className="wl-abschnittskopf">
          <h2 className="wl-abschnittstitel">{t("Kritiken")}</h2>
          <Btn variante="voll" onClick={() => oeffneModal({ typ: "kritik", kontext })}>{t("Kritik erfassen")}</Btn>
        </div>
        {kritiken.length === 0 ? (
          <Leer text={t("Für diesen Jahrgang ist noch keine Kritik erfasst.")} />
        ) : (
          <Begrenzt liste={kritiken} grenze={G.kritiken} schluessel={jg.id}>{(zeilen) => (
          <div className="wl-scroll">
            <table className="wl-tabelle">
              <thead><tr><th>{t("Kritiker / Quelle")}</th><th>{t("Bewertung")}</th><th>{t("Anlass / Pairing")}</th><th>{t("Notiz")}</th><th className="wl-rechts"> </th></tr></thead>
              <tbody>
                {zeilen.map((k) => (
                  <tr key={k.id}>
                    <td>{k.kritiker}<span className="wl-nebenzeile">{SCHEMATA[k.schema] ? SCHEMATA[k.schema].label : k.schema}</span></td>
                    <td><span className="wl-bewertung num">{wertFormat(k.schema, k.wert)}</span><KritikBalken anteil={normiert(k.schema, k.wert)} /></td>
                    <td>{(k.anlass || k.pairing) ? <>{k.anlass && <span>{k.anlass}</span>}{k.anlass && k.pairing && <span className="wl-nebenzeile">{k.pairing}</span>}{!k.anlass && k.pairing && <span>{k.pairing}</span>}</> : "—"}</td>
                    <td className="wl-notiz">{k.text || "—"}</td>
                    <td className="wl-rechts"><TextKnopf onClick={() => oeffneModal({ typ: "kritik", objekt: k, kontext })}>{t("Bearbeiten")}</TextKnopf></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}</Begrenzt>
        )}
      </section>

      {region && jg.jahr !== NV && (
        <section className="wl-abschnitt">
          {darfWetter && <WitterungsPanel region={region} db={db} setDb={setDb} vorwahlJahr={jg.jahr} />}
        </section>
      )}
      {region && jg.jahr === NV && (
        <p className="wl-hinweis">{t("Für Weine ohne Jahrgang (N.V.) gibt es keine jahresbezogene Witterung.")}</p>
      )}

      <BewegungsHistorie db={db} weinId={wein.id} jgId={jg.id} oeffneModal={oeffneModal} />
    </>
  );
}

export { EtikettBild, JahrgangDetail, JahrgangDetailInhalt, RundumBild };
